import {
  abcAnalysis, materialReconciliation, materialRequirementPlan, poExpediting, stockAuditStatus, stockLedger,
  vendorScorecard,
} from './supply-chain.js'
import {
  approvalTracker, billingReconciliation, completedProfitability, costVariance, profitAnalysis, projectFinancials,
  regulariseBoq,
} from './project.js'
import { defineReport, rc, rf, type ReportDef } from './types.js'
// The operational, inventory, tools and integration reports live in a file
// of their own (docs/25); they are listed here in menu order like the rest.
import {
  deliveryChallanStatus, employeeAdvances, integrationStatus, labourPendingSco, materialInwardOutward,
  materialIssuedToSubcontractor, materialPendingRequisition, materialProgress, materialPurchaseForecast,
  materialRequestExecution, projectExpenses, purchaseOrderExecution, purchasePriceHistory, purchaseSavings,
  stockAgeing, stockAvailability, stockPrExecution, stockPurchasePlanner, stockValuation, subcontractExecution,
  toolAvailability,
} from './operations.js'
import { DOCUMENTS } from '../documents/registry.js'

/** Every document type, for the Document type filters. */
const DOC_TYPE_OPTIONS = DOCUMENTS.map((d) => ({ value: d.key, label: d.label }))

/* ================= Management ================= */

// Financials, Completed profitability and the Approval tracker live in ./project.ts.

/* ================= Project ================= */

// Profitability, Cost variance, Regularise BOQ and Billing reconciliation live in ./project.ts.

/* ================= Operation ================= */

/**
 * What each contractor got done on a project, side by side.
 *
 * The customer runs four contractors on one site at once and asks the same
 * question of all of them daily: is this moving? Subcontract Order Execution
 * cannot answer it, because three of the four have no order
 * (docs/video-notes/06 §3). Billable lines only — nobody measures the painting.
 */
export const contractorProgress = defineReport({
  key: 'contractor_progress', route: 'contractor_progress', group: 'Operation',
  label: 'Work progress by contractor',
  blurb: 'What each contractor has completed on a project, whether or not an order was issued.',
  filters: [
    rf.project(true), rf.period('Progress recorded', false),
    { key: 'supplierId', label: 'Contractor', control: 'lookup', lookup: { entity: 'subcontractor' } },
  ],
  columns: [
    rc.text('supplierName', 'Contractor', 220), rc.text('boqRef', 'BOQ no', 120),
    rc.text('itemName', 'Work'),
    rc.qty('estimatedQty', 'Estimated'), rc.qty('completedQty', 'Completed'),
    rc.pct('completedPct', 'Done %'),
    rc.text('againstOrder', 'Order', 150),
    rc.date('lastEntry', 'Last entry'),
  ],
} as const)

/**
 * How many people turned up, by day and by contractor.
 *
 * Asked for outright and answered by nothing in the flow chart: "our daily
 * labour count is important — we are not giving it like a lump sum, we have to
 * calculate how many people are coming in daily" (docs/video-notes/06 §5).
 */
export const labourHeadcount = defineReport({
  key: 'labour_headcount', route: 'labour_headcount', group: 'Operation',
  label: 'Daily labour headcount',
  blurb: 'How many people each contractor had on site, day by day.',
  filters: [
    rf.project(true), rf.period('Between', false),
    { key: 'supplierId', label: 'Contractor', control: 'lookup', lookup: { entity: 'subcontractor' } },
  ],
  columns: [
    rc.date('documentDate', 'Date'), rc.text('supplierName', 'Contractor', 240),
    rc.doc(), rc.qty('labourCount', 'People'), rc.qty('labourHours', 'Hours'),
  ],
} as const)

/**
 * Everything bought in a period, as a register.
 *
 * Distinct from Purchase price records, which is item-centric and answers
 * "what has this cost before". This one answers "what did we buy last month,
 * from whom, on which order" — the question the purchase team is asked at a
 * review, and the vendor's own description: *"historical information, what PO
 * you bought before and who you bought from"* (docs/video-notes/06 §9).
 */
export const materialPurchaseRecord = defineReport({
  key: 'material_purchase_record', route: 'material_purchase_record', group: 'Operation',
  label: 'Material purchase record',
  blurb: 'Every purchase made in a period, with its order, supplier, rate and value.',
  filters: [
    rf.period('Ordered between', false), { ...rf.project(), multiple: true }, rf.supplier(),
    { ...rf.category(), multiple: true },
  ],
  columns: [
    rc.date('documentDate', 'Date'), rc.doc(), rc.text('supplierName', 'Supplier', 220),
    rc.text('itemName', 'Material'), rc.text('projectName', 'Project', 200),
    rc.qty('quantity', 'Qty'), rc.text('unit', 'Unit', 90),
    rc.money('rate', 'Rate'), rc.money('amount', 'Value'),
  ],
} as const)

export const REPORT_DEFS = [
  projectFinancials, completedProfitability, approvalTracker,
  profitAnalysis, costVariance, regulariseBoq, billingReconciliation, projectExpenses,
  materialPendingRequisition, labourPendingSco, materialRequestExecution,
  purchaseOrderExecution, subcontractExecution, materialProgress,
  materialIssuedToSubcontractor, contractorProgress, labourHeadcount,
  deliveryChallanStatus, purchaseSavings,
  purchasePriceHistory, materialPurchaseRecord, materialPurchaseForecast, employeeAdvances,
  stockAvailability, stockPurchasePlanner, stockPrExecution,
  materialInwardOutward, stockValuation, stockAgeing,
  // docs/24 — the supply chain after the purchase order.
  materialRequirementPlan, abcAnalysis, vendorScorecard, poExpediting, materialReconciliation, stockLedger,
  stockAuditStatus,
  toolAvailability, integrationStatus,
] as const satisfies readonly ReportDef[]

export {
  deliveryChallanStatus, employeeAdvances, integrationStatus, labourPendingSco, materialInwardOutward,
  materialIssuedToSubcontractor, materialPendingRequisition, materialProgress, materialPurchaseForecast,
  materialRequestExecution, projectExpenses, purchaseOrderExecution, purchasePriceHistory, purchaseSavings,
  stockAgeing, stockAvailability, stockPrExecution, stockPurchasePlanner, stockValuation, subcontractExecution,
  toolAvailability,
}
export {
  APPROVAL_DOC_TYPE_OPTIONS, approvalTracker, billingReconciliation, completedProfitability, costVariance, profitAnalysis,
  projectFinancials, regulariseBoq,
} from './project.js'

const byKey = new Map<string, ReportDef>(REPORT_DEFS.map((d) => [d.key, d]))
const byRoute = new Map<string, ReportDef>(REPORT_DEFS.map((d) => [d.route, d]))
export const getReportDef = (key: string): ReportDef | undefined => byKey.get(key)
export const getReportDefByRoute = (route: string): ReportDef | undefined => byRoute.get(route)
